/**
 * Dashboard Selector
 * Shows available dashboards — legal-grade formal aesthetic
 */

import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/auth-context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DollarSign,
  FileText,
  Users,
  FolderKanban,
  Repeat,
  BarChart3,
  LogOut,
  HeadphonesIcon,
  Settings,
} from 'lucide-react';
import { DashboardLabels, DashboardDescriptions, type DashboardTypeValue } from '@bizsolutions/shared';

const dashboardIcons: Record<DashboardTypeValue, React.ReactNode> = {
  customer_operations: <Users className="h-6 w-6" />,
  helpdesk_support: <HeadphonesIcon className="h-6 w-6" />,
  billing_finance: <DollarSign className="h-6 w-6" />,
  tax_compliance: <FileText className="h-6 w-6" />,
  project_management: <FolderKanban className="h-6 w-6" />,
  service_delivery: <Repeat className="h-6 w-6" />,
  analytics: <BarChart3 className="h-6 w-6" />,
  system_admin: <Settings className="h-6 w-6" />,
};

const dashboardRoutes: Record<DashboardTypeValue, string> = {
  customer_operations: '/customer-operations',
  helpdesk_support: '/helpdesk',
  billing_finance: '/billing',
  tax_compliance: '/tax',
  project_management: '/projects',
  service_delivery: '/service',
  analytics: '/analytics',
  system_admin: '/admin',
};

export function DashboardSelector() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const availableDashboards = Array.isArray(user?.dashboardAccess)
    ? (user.dashboardAccess as DashboardTypeValue[])
    : [];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-legal-warm/40">
      <header className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-sidebar.png" alt="BizSolutionsMate" className="h-8 w-auto" />
            <div>
              <h1 className="text-xl font-serif font-semibold tracking-tight">BizSolutionsMate</h1>
              <p className="text-xs text-primary-foreground/60 mt-0.5">
                Welcome back, {user?.firstName} {user?.lastName}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="border-white/20 text-primary-foreground bg-transparent hover:bg-white/10 hover:text-primary-foreground"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign out
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-10">
        <div className="mb-8">
          <h2 className="text-xl font-serif font-semibold mb-1.5">Select a Dashboard</h2>
          <p className="text-sm text-muted-foreground">
            Choose a dashboard to manage your business operations
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {availableDashboards.map((dashboard) => (
            <Card
              key={dashboard}
              className="cursor-pointer group hover:border-legal-gold/50 transition-all hover:shadow-legal-md"
              onClick={() => navigate(dashboardRoutes[dashboard])}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/5 rounded text-primary group-hover:bg-primary/10 transition-colors">
                    {dashboardIcons[dashboard]}
                  </div>
                  <CardTitle className="text-base font-serif">{DashboardLabels[dashboard]}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-[13px] leading-relaxed">{DashboardDescriptions[dashboard]}</CardDescription>
              </CardContent>
            </Card>
          ))}

          {availableDashboards.length === 0 && (
            <Card className="col-span-full">
              <CardContent className="py-10 text-center text-muted-foreground text-sm">
                No dashboards available. Please contact your administrator.
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
